import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import useChatStore from "@/store/chatStore";
import { playMessageSound } from "@/utils/sound";
import { notificationText } from "@/utils/chat";

export const CHAT_PATH = "/deposit-withdraw";

const VISIBLE_INTERVAL_MS = 10000;
const HIDDEN_INTERVAL_MS = 30000;
const ERROR_INTERVAL_MS = 60000;

function canUseBrowserNotifications() {
  return typeof window !== "undefined" && "Notification" in window && Notification.permission === "granted";
}

function ToastBody({ title, text }) {
  return (
    <div dir="rtl" className="space-y-0.5 text-right">
      <div className="text-sm font-semibold">{title}</div>
      <div className="line-clamp-2 text-xs text-muted-foreground">{text}</div>
    </div>
  );
}

/**
 * Polls the unread counter for «واریز و برداشت» and announces new messages with a sound,
 * an in-app toast and (when the tab is in the background) a browser notification.
 */
export default function ChatNotifier() {
  const navigate = useNavigate();
  const lastSeenIdRef = useRef(null);
  const timerRef = useRef(null);
  const baseTitleRef = useRef(typeof document !== "undefined" ? document.title.replace(/^\(\d+\+?\)\s*/, "") : "");

  const unreadTotal = useChatStore((s) => s.unreadTotal);

  // Telegram-web style "(3) Title" so the badge is visible on the browser tab too
  useEffect(() => {
    const base = baseTitleRef.current || document.title;
    document.title = unreadTotal > 0 ? `(${unreadTotal > 99 ? "99+" : unreadTotal}) ${base}` : base;
  }, [unreadTotal]);

  useEffect(() => () => {
    document.title = baseTitleRef.current || document.title;
  }, []);

  useEffect(() => {
    let cancelled = false;

    const announce = async () => {
      const store = useChatStore.getState();
      const conversations = await store.refreshConversations();
      // newest unread conversation is the one that triggered the notification
      const target = conversations
        .filter((c) => c.unreadCount > 0 && c.lastMessage && !c.lastMessage.isMine)
        .sort((a, b) => (b.lastMessage?.id ?? 0) - (a.lastMessage?.id ?? 0))[0];
      if (!target) return;

      const viewingIt = store.pageOpen && store.activeId === target.id && document.visibilityState === "visible";
      if (viewingIt) return;

      if (store.soundEnabled) playMessageSound();
      const text = notificationText(target);
      const openChat = () => navigate(`${CHAT_PATH}?c=${target.id}`);

      if (document.visibilityState === "visible") {
        toast.info(<ToastBody title={target.title} text={text} />, {
          toastId: `chat-${target.lastMessage.id}`,
          icon: false,
          autoClose: 6000,
          onClick: openChat,
        });
      } else if (canUseBrowserNotifications()) {
        try {
          const notification = new Notification(target.title, {
            body: text,
            tag: `chat-${target.id}`,
            icon: "/favicon.svg",
            dir: "rtl",
            lang: "fa",
          });
          notification.onclick = () => {
            window.focus();
            openChat();
            notification.close();
          };
        } catch {
          // some mobile browsers only allow notifications from a service worker
        }
      }
    };

    const tick = async () => {
      let delay = document.visibilityState === "visible" ? VISIBLE_INTERVAL_MS : HIDDEN_INTERVAL_MS;
      try {
        const data = await useChatStore.getState().refreshUnread();
        const latest = data?.latestMessageId ?? 0;
        // the first poll only establishes a baseline so old messages do not ring on login
        if (lastSeenIdRef.current !== null && latest > lastSeenIdRef.current) await announce();
        lastSeenIdRef.current = Math.max(lastSeenIdRef.current ?? 0, latest);
      } catch {
        delay = ERROR_INTERVAL_MS;
      }
      if (!cancelled) timerRef.current = setTimeout(tick, delay);
    };

    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      clearTimeout(timerRef.current);
      tick();
    };

    tick();
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancelled = true;
      clearTimeout(timerRef.current);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [navigate]);

  return null;
}
